import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  FOTO_FLAT,
  GIFTCARD,
  SINGLE_PHOTO,
  PHOTO_DVD,
  OTHER_PRODUCTS,
  PARTICIPANT,
  PRICELIST,
} from '../../../utils/variables';
import { FormattedMessageWrappedInSpan } from '../../misc';
import FormattedPrice from '../../misc/FormattedPrice';
import SubsectionInFrame from '../../misc/SubsectionInFrame';

const OfferTile = ({
  product,
  componentVersion,
  fotoFlatPrice,
  hasVideo,
  hasVideoUrl,
  onVideoClick,
  hasCertificates,
  onCertificateClick,
  voucherCampaigns,
  onVoucherClick,
  products,
  onBuy,
  onPriceClick,
  showOfferHintForStillProcessingMedia,
  participantMediaCount,
  impressionMediaCount,
  onImpressionsClick,
  onGoToSearchResults,
}) => {
  const offerRef = useRef();

  const renderTitle = () => {
    switch (product) {
      case GIFTCARD:
        return (
          <FormattedMessageWrappedInSpan
            id="offerTile.giftcardOffer"
            defaultMessage="Giftcard"
            className="typography uppercase typography-title_small"
          />
        );
      case FOTO_FLAT:
        return (
          <FormattedMessageWrappedInSpan
            id="global.fotoFlat"
            defaultMessage="Foto-Flat"
            className="typography uppercase typography-title_small"
          />
        );
      default:
        return (
          <FormattedMessageWrappedInSpan
            id="offerTile.otherProductsTitle"
            defaultMessage="Other products"
            className="typography uppercase typography-title_small"
          />
        );
    }
  };

  const renderButtonText = () => {
    switch (product) {
      case GIFTCARD:
        return (
          <FormattedMessageWrappedInSpan
            id="offerTile.button.giftcardAction"
            defaultMessage="Buy as a gift"
            className="typography bold-text"
          />
        );
      case FOTO_FLAT:
        return (
          <FormattedMessageWrappedInSpan
            id="offerTile.button.fotoFlatAction"
            defaultMessage="Buy now"
            className={classNames('typography bold-text', {
              'typography-title_xsmall': componentVersion === PARTICIPANT,
            })}
          />
        );
    }
  };

  const renderPersonalMediaText = () => {
    if (showOfferHintForStillProcessingMedia) {
      return (
        <FormattedMessageWrappedInSpan
          id="offerTile.description.personalPhotos.pendingMedia"
          defaultMessage="All {count} personal digital photos (JPG) and <b>all coming personal digital photos (with no extra cost)</b>"
          values={{
            count: participantMediaCount > 0 ? participantMediaCount : '',
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      );
    } else if (componentVersion === PARTICIPANT) {
      return (
        <FormattedMessageWrappedInSpan
          id="offerTile.description.personalPhotos.withCount"
          defaultMessage="All {count} personal digital fotos (JPG)"
          values={{ count: participantMediaCount > 0 ? participantMediaCount : '' }}
        />
      );
    } else {
      return (
        <FormattedMessageWrappedInSpan
          id="offerTile.description.personalPhotos.withoutCount"
          defaultMessage="All personal digital photos (JPG)"
        />
      );
    }
  };

  return (
    <div ref={offerRef} className="subsection-full-width-sm">
      <SubsectionInFrame
        className={classNames('offerTile', {
          'pricelist-first-row': product === GIFTCARD,
          'subsection-animated': componentVersion != PRICELIST,
          
        })}
        actionable={
          (onBuy && componentVersion === PARTICIPANT) ||
          (onGoToSearchResults && componentVersion != PARTICIPANT)
        }
        twoRows={Boolean(fotoFlatPrice)}
        title={
          <>
            {renderTitle()}
            {fotoFlatPrice && (
              <span className="subsection-main-price typography bold-text">
                <FormattedPrice price={fotoFlatPrice} />
              </span>
            )}
          </>
        }
        onTitleClick={() => onPriceClick && !onBuy && onPriceClick()}
        lastInRow
      >
        {product === FOTO_FLAT && (
          <>
            <div className="subsection-item">{renderPersonalMediaText()}</div>
            {(impressionMediaCount === undefined || impressionMediaCount > 0) && (
              <div
                className={classNames('subsection-item', {
                  'subsection-item_actionable': Boolean(onImpressionsClick),
                })}
                onClick={() => {
                  onImpressionsClick && onImpressionsClick();
                }}
              >
                <FormattedMessageWrappedInSpan
                  id="offerTile.description.impressionPhotos"
                  defaultMessage="All {count} digital impressions (JPG)"
                  values={{ count: impressionMediaCount }}
                />
                {onImpressionsClick && <span className="icon eye" />}
              </div>
            )}
            {hasVideo && (
              <div
                className={classNames('subsection-item', {
                  'subsection-item_actionable': hasVideoUrl && onVideoClick,
                })}
                onClick={() => {
                  if (hasVideoUrl && onVideoClick) {
                    onVideoClick();
                  }
                }}
              >
                <FormattedMessageWrappedInSpan
                  id="offerTile.description.videoOffert"
                  defaultMessage="Impression video with <b>your personal finish scene</b> (MP4)"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                  className={classNames({
                    'subsection-item-text_actionable': hasVideoUrl,
                  })}
                />
                {hasVideoUrl && onVideoClick && <span className="icon eye" />}
              </div>
            )}
            {hasCertificates && (
              <div
                className={classNames('subsection-item', {
                  'subsection-item_actionable': onCertificateClick,
                })}
                onClick={() => {
                  onCertificateClick && onCertificateClick();
                }}
              >
                <FormattedMessageWrappedInSpan
                  id="offerTile.description.certificatesOffert"
                  defaultMessage="Printable certificate (JPEG)"
                  className={classNames({
                    'subsection-item-text_actionable': onCertificateClick,
                  })}
                />
                {onCertificateClick && <span className="icon eye" />}
              </div>
            )}
            {voucherCampaigns &&
              voucherCampaigns.map((voucherCampaign) => {
                return (
                  <div
                    key={voucherCampaign.id}
                    className={classNames('subsection-item subsection-item-voucher', {
                      'subsection-item-text_actionable': onVoucherClick,
                    })}
                    onClick={() => {
                      onVoucherClick({ voucherCampaign });
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: voucherCampaign.shortText,
                      }}
                    />
                    <span className="icon eye" />
                  </div>
                );
              })}
          </>
        )}
        {product === GIFTCARD && (
          <div className="subsection-item">
            <FormattedMessageWrappedInSpan
              id="offerTile.description.giftcard"
              defaultMessage="Sharable code to redeem one Foto-Flat from this event with all extras included"
            />
          </div>
        )}
        {product === OTHER_PRODUCTS && (
          <>
            {products.map((p) => {
              if (p.type === SINGLE_PHOTO && !p.mediaCollection) {
                return (
                  <div key={p.id} className="subsection-item">
                    <FormattedMessageWrappedInSpan
                      id="offerTile.description.singlePhoto"
                      defaultMessage="1 personal digital photo (JPG)"
                    />
                    <span>
                      <FormattedPrice price={p.price} />
                    </span>
                  </div>
                );
              }
              if (p.type === PHOTO_DVD) {
                return (
                  <div key={p.id} className="subsection-item">
                    <FormattedMessageWrappedInSpan
                      id="offerTile.description.additionalShipment"
                      defaultMessage="Optional: shipping of your order on DVD"
                    />
                    <span>
                      <FormattedPrice price={p.price} />
                    </span>
                  </div>
                );
              }
            })}
          </>
        )}
        {onBuy && componentVersion === PARTICIPANT && (
          <button
            className={classNames('subsection-action-button btn-in-media-list warning-btn', {
              'subsection-action-button_small': componentVersion != PRICELIST,
            })}
            onClick={onBuy}
          >
            {renderButtonText()}
          </button>
        )}
         {onBuy && product === GIFTCARD && (
          <button
            className={classNames('subsection-action-button btn-in-media-list primary-new')}
            onClick={onBuy}
          >
            {renderButtonText()}
          </button>
        )}
        {onGoToSearchResults && componentVersion != PARTICIPANT && (
          <button
            className={classNames(
              'subsection-action-button primary-new btn-in-media-list subsection-action-button_small'
            )}
            onClick={() => onGoToSearchResults()}
          >
            <FormattedMessageWrappedInSpan
              id="offerTile.button.useMainSearch"
              defaultMessage="Use main search to buy"
              className="typography bold-text typography-title_xsmall"
            />
          </button>
        )}
      </SubsectionInFrame>
    </div>
  );
};

OfferTile.propTypes = {
  product: PropTypes.string,
  componentVersion: PropTypes.string,
  fotoFlatPrice: PropTypes.object,
  hasVideo: PropTypes.bool,
  hasVideoUrl: PropTypes.bool,
  onVideoClick: PropTypes.func,
  hasCertificates: PropTypes.bool,
  onCertificateClick: PropTypes.func,
  voucherCampaigns: PropTypes.array,
  onVoucherClick: PropTypes.func,
  products: PropTypes.array,
  onBuy: PropTypes.func,
  onPriceClick: PropTypes.func,
  showOfferHintForStillProcessingMedia: PropTypes.bool,
  participantMediaCount: PropTypes.number,
  impressionMediaCount: PropTypes.number,
  onImpressionsClick: PropTypes.func,
  onGoToSearchResults: PropTypes.func,
};

export { OfferTile };

export default OfferTile;
